import React from 'react';
import {
    DollarSign,
    TrendingUp,
    TrendingDown,
    Target,
    BarChart3,
    AlertTriangle
} from 'lucide-react';
import { formatCurrency, formatNumber } from '../../utils/helpers';

const HealthMetricsBar = ({
    metrics,
    overallROAS = 0,
    activeCampaigns = 0,
    totalCampaigns = 0,
    alertCount = 0,
    loading,
    trends = {}
}) => {
    // Safe metrics with defaults
    const safeMetrics = metrics || { spend: 0, revenue: 0, impressions: 0, clicks: 0, conversions: 0 };
    const safeROAS = overallROAS || 0;

    const getRoasColor = (roas) => {
        if (roas >= 3) return 'var(--color-success)';
        if (roas >= 1) return 'var(--color-warning)';
        return 'var(--color-error)';
    };

    const getRoasLabel = (roas) => {
        if (roas >= 3) return 'Excellent';
        if (roas >= 2) return 'Good';
        if (roas >= 1) return 'Break-even';
        return 'Losing money';
    };

    if (loading) {
        return (
            <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(5, 1fr)',
                gap: 'var(--space-4)',
                marginBottom: 'var(--space-6)'
            }}>
                {[1, 2, 3, 4, 5].map(i => (
                    <div
                        key={i}
                        style={{
                            background: 'var(--color-surface)',
                            borderRadius: 'var(--radius-lg)',
                            padding: 'var(--space-4)',
                            height: '90px',
                            animation: 'pulse 2s infinite'
                        }}
                    />
                ))}
            </div>
        );
    }

    const metricCards = [
        {
            label: 'Total Spend',
            value: formatCurrency(safeMetrics.spend),
            icon: DollarSign,
            color: 'var(--color-text-primary)',
            trend: trends.spend
        },
        {
            label: 'Total Revenue',
            value: formatCurrency(safeMetrics.revenue),
            icon: TrendingUp,
            color: 'var(--color-success)',
            trend: trends.revenue
        },
        {
            label: 'Overall ROAS',
            value: `${safeROAS.toFixed(2)}x`,
            subLabel: getRoasLabel(safeROAS),
            icon: Target,
            color: getRoasColor(safeROAS),
            trend: trends.roas
        },
        {
            label: 'Active Campaigns',
            value: `${activeCampaigns}/${totalCampaigns}`,
            icon: BarChart3,
            color: 'var(--color-primary)'
        },
        {
            label: 'Alerts',
            value: alertCount,
            icon: AlertTriangle,
            color: alertCount > 0 ? 'var(--color-error)' : 'var(--color-success)',
            highlight: alertCount > 0
        }
    ];

    return (
        <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
            gap: 'var(--space-4)',
            marginBottom: 'var(--space-6)'
        }}>
            {metricCards.map((card, index) => (
                <div
                    key={card.label}
                    style={{
                        background: card.highlight
                            ? 'linear-gradient(135deg, rgba(239, 68, 68, 0.1), rgba(239, 68, 68, 0.05))'
                            : 'var(--color-surface)',
                        border: card.highlight
                            ? '1px solid rgba(239, 68, 68, 0.3)'
                            : '1px solid var(--color-border)',
                        borderRadius: 'var(--radius-lg)',
                        padding: 'var(--space-4)',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 'var(--space-2)'
                    }}
                >
                    <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                    }}>
                        <span style={{
                            fontSize: 'var(--font-size-xs)',
                            color: 'var(--color-text-tertiary)',
                            textTransform: 'uppercase',
                            letterSpacing: '0.05em'
                        }}>
                            {card.label}
                        </span>
                        <card.icon size={16} style={{ color: card.color }} />
                    </div>
                    <div style={{
                        display: 'flex',
                        alignItems: 'baseline',
                        gap: 'var(--space-2)'
                    }}>
                        <div style={{
                            fontSize: 'var(--font-size-xl)',
                            fontWeight: 700,
                            color: card.color
                        }}>
                            {card.value}
                        </div>
                        {card.trend && card.trend.direction !== 'neutral' && (
                            <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '2px',
                                fontSize: 'var(--font-size-xs)',
                                fontWeight: 500,
                                color: card.trend.color
                            }}>
                                {card.trend.direction === 'up' ? (
                                    <TrendingUp size={12} />
                                ) : (
                                    <TrendingDown size={12} />
                                )}
                                {card.trend.percentage.toFixed(1)}%
                            </div>
                        )}
                    </div>
                    {card.subLabel && (
                        <div style={{
                            fontSize: 'var(--font-size-xs)',
                            color: card.color,
                            opacity: 0.8
                        }}>
                            {card.subLabel}
                        </div>
                    )}
                </div>
            ))}
        </div>
    );
};

export default HealthMetricsBar;
